import { UseFormReturn } from 'react-hook-form';
import {
    AttributeOption,
    CallForServiceOption,
    EventLabelOption,
    EventPanelFormType,
} from '@mark43/cad-api';
import {
    CallForServiceOptionWithPriority,
    getHighestEventLabelPriorityOption,
    getHighestPriorityCallForService,
    toCallForServiceOptionWithPriority,
} from '../../event-panel/event-panel-form/helpers/prefill-helpers';

export function prefillAttributeFieldBasedOnId({
    prefillField,
    prefillOptionId,
    options,
    form,
}: {
    prefillField: keyof EventPanelFormType;
    prefillOptionId?: number;
    options?: AttributeOption[];
    form: UseFormReturn<EventPanelFormType>;
}) {
    if (!prefillOptionId || !options) {
        return;
    }

    const prefillOption = options.find((option) => option.value === prefillOptionId);

    if (prefillOption) {
        form.setValue(prefillField, prefillOption);
    }
}

export function getPriorityPrefill(
    currentPriority: AttributeOption | undefined,
    callForService: CallForServiceOption | undefined,
    additionalCallForServices: CallForServiceOption[],
    eventLabels: EventLabelOption[],
    priorityOptions: AttributeOption[]
): AttributeOption | undefined {
    const callForServiceWithPriority = toCallForServiceOptionWithPriority(
        callForService,
        priorityOptions
    );
    const additionalCallForServicesWithPriority = additionalCallForServices
        .map((cfs) => toCallForServiceOptionWithPriority(cfs, priorityOptions))
        .filter((cfs): cfs is CallForServiceOptionWithPriority => cfs !== undefined);

    const highestPriorityCallForService = getHighestPriorityCallForService(
        callForServiceWithPriority,
        additionalCallForServicesWithPriority
    );

    // These are parentAttrIds, not values.
    const highestCallForServicePriority = highestPriorityCallForService?.priorityGlobalAttrId;
    const highestEventLabelPriorityAttributeOption = getHighestEventLabelPriorityOption(
        eventLabels,
        priorityOptions
    );

    const eventLabelPriorityNotFound = highestEventLabelPriorityAttributeOption === undefined;
    if (currentPriority && eventLabelPriorityNotFound) {
        // if there is a priority set and event labels have no priority. return current priority without change to default
        return currentPriority;
    }

    // prefer call for service priority if there is a tie
    const callForServiceHasHigherPriorityThanEventLabel =
        highestCallForServicePriority &&
        highestEventLabelPriorityAttributeOption?.parentAttributeId &&
        // lower id means higher priority
        highestCallForServicePriority <=
            highestEventLabelPriorityAttributeOption?.parentAttributeId;

    if (
        highestCallForServicePriority &&
        (eventLabelPriorityNotFound || callForServiceHasHigherPriorityThanEventLabel)
    ) {
        // if highestEventLabelPriorityAttributeOption is not present
        // OR it has lower priority than highestCallForServicePriority
        // use highestPriorityCallForService
        return priorityOptions.find(
            (priorityOption) =>
                priorityOption.value === highestPriorityCallForService?.priorityAttrId
        );
    }

    if (highestEventLabelPriorityAttributeOption?.parentAttributeId) {
        return highestEventLabelPriorityAttributeOption
    }

    return;
}
